{
  "blocks": [],
  "relationships": [],
  "localeVariants": [
    {
      "id": "moon-article-traveling-for-fertility-treatment",
      "locale": "fr",
      "variant": {
        "title": "Voyager pour un traitement de fertilité",
        "subtitle": "Un plan pratique pour coordonner une clinique éloignée, un suivi local et les déplacements liés au traitement.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Il existe deux types de parcours à distance.",
              "description": "Tu peux voyager vers une clinique éloignée tout en faisant ton suivi près de chez toi, ou traverser une frontière pour une partie ou la totalité des soins."
            },
            {
              "title": "Planifie chaque déplacement avant de réserver.",
              "description": "Demande quelles visites sont indispensables, lesquelles peuvent avoir lieu localement, et quoi faire si le cycle ou un vol est modifié."
            },
            {
              "title": "La clinique dirige le calendrier du traitement.",
              "description": "Obtiens des consignes écrites sur le suivi, les horaires des médicaments, l'aide en dehors des heures d'ouverture et le professionnel local qui interprétera les résultats."
            }
          ],
          "sections": [
            {
              "title": "Qu'est-ce qu'un parcours à distance ?",
              "content": "Tu peux vivre loin de ta clinique de fertilité, utiliser une clinique locale pour les prises de sang ou les échographies, ou traverser une frontière pour ton traitement. Certaines personnes voyagent uniquement pour la ponction ou le transfert ; d'autres pour tout le parcours. Avant de t'engager, demande à la clinique de décrire le parcours complet dans un calendrier, pas seulement l'acte médical.",
              "bulletPoints": [
                "Soins à distance : la clinique principale dirige le traitement et un professionnel local assure le suivi",
                "Déplacement pour le traitement : tu voyages pour une ou plusieurs étapes sensibles au calendrier",
                "Parcours transfrontalier : les lois, dossiers, assurances et soins de suivi peuvent concerner plusieurs pays"
              ]
            },
            {
              "title": "Choisir l'organisation des soins avant de réserver",
              "content": "Demande si la clinique accepte le suivi réalisé ailleurs et exactement quels éléments elle attend du professionnel local. Vérifie qui examine chaque résultat, sous quel délai les résultats doivent être transmis, quel fuseau horaire est utilisé et qui peut modifier ton traitement. Une échographie ou une prise de sang locale n'est utile que si la clinique qui te traite peut la recevoir et agir à temps.",
              "bulletPoints": [
                "Le suivi peut-il avoir lieu dans une clinique de fertilité, un hôpital ou un centre d'imagerie local ?",
                "Quels dosages et quelles mesures échographiques la clinique demande-t-elle ?",
                "Qui envoie les résultats, par quel moyen sécurisé et avant quelle heure ?",
                "Quel est le contact en dehors des heures d'ouverture si un résultat modifie le plan ?"
              ]
            },
            {
              "title": "Cartographier chaque rendez-vous et déplacement",
              "content": "Construis le calendrier avec la clinique avant de payer les billets. Inclue la première consultation, le bilan de début de cycle, les suivis, le déclenchement et la ponction, le transfert, le test de grossesse et tout suivi nécessaire. Demande quel préavis tu peux avoir avant une ponction ou un transfert : la croissance folliculaire et la réponse de l'endomètre peuvent modifier le calendrier.",
              "bulletPoints": [
                "Visites obligatoires et heure d'arrivée la plus tôt ou la plus tard possible",
                "Visites pouvant être réalisées localement ou en téléconsultation",
                "Marge pour un retard, un transfert annulé, un suivi supplémentaire ou une nuit de plus",
                "Plan pour le retour et pour le professionnel qui recevra les résultats ensuite"
              ]
            },
            {
              "title": "Rendre les horaires et la communication explicites",
              "content": "Les fuseaux horaires comptent lorsqu'une injection, un déclenchement, une ponction, un transfert ou un résultat de laboratoire doit respecter une fenêtre précise. Demande un calendrier écrit avec le fuseau horaire et la conduite à tenir si tu rates un appel, perds internet, es retardée ou ne peux pas faire un rendez-vous local. Ne convertis pas seule l'heure d'un déclenchement ou d'une autre dose sensible.",
              "bulletPoints": [
                "Numéro de la clinique, messagerie sécurisée et numéro d'urgence",
                "Fuseau horaire utilisé pour chaque médicament et rendez-vous",
                "Symptômes urgents et lieu où consulter sur place",
                "Personne autorisée à suspendre, modifier ou annuler le traitement"
              ]
            },
            {
              "title": "Préparer les dossiers, la langue et le consentement",
              "content": "Garde un dossier de voyage avec ton plan de traitement, la liste des médicaments sous leurs noms génériques, tes allergies, les résultats utiles, tes antécédents de procédure, les coordonnées et les informations d'assurance. Demande si les dossiers doivent être traduits ou présentés dans un format précis. Pour un don, des embryons, des ovocytes, du sperme ou une gestatrice, demande un avis juridique dans le pays de destination et dans ton pays de résidence : les règles ne suivent pas toujours le plan médical.",
              "bulletPoints": [
                "Coordonnées de la clinique et du professionnel local, y compris les contacts d'urgence",
                "Liste de médicaments, ordonnances et lettre expliquant les médicaments injectables",
                "Copies des résultats, imageries, comptes rendus opératoires et consentements",
                "Accès sécurisé aux dossiers sans dépendre d'un seul appareil"
              ]
            },
            {
              "title": "Budgéter tout le parcours",
              "content": "Compare le coût complet du parcours, pas seulement le prix annoncé de la procédure. Ajoute les médicaments, le suivi local, les vols, l'hébergement, les repas, la garde d'enfants, les jours de congé, la traduction, la conservation, les frais de ponction ou de transfert et une réserve pour un retard ou une urgence. Demande ce qui se passe financièrement si le cycle est annulé, si le transfert est reporté ou si tu dois rester plus longtemps.",
              "bulletPoints": [
                "Devis écrit séparant les éléments nécessaires et optionnels",
                "Suivi, anesthésie, laboratoire, conservation et suivi après traitement",
                "Règles de modification ou d'annulation des vols et de l'hébergement",
                "Exclusions d'assurance liées aux pathologies préexistantes ou au traitement de fertilité"
              ]
            },
            {
              "title": "Si le traitement traverse une frontière",
              "content": "Vérifie comment le traitement de fertilité est encadré dans le pays de destination et quelles normes s'appliquent à la clinique et au laboratoire. Demande comment les taux de réussite sont calculés, quelle est la politique concernant les grossesses multiples, comment les réclamations sont traitées et qui prendra en charge une complication après ton retour. Si des gamètes, embryons ou une gestatrice sont concernés, obtiens un avis juridique qualifié avant le traitement.",
              "bulletPoints": [
                "Existe-t-il un organisme national de contrôle ou d'accréditation que tu peux vérifier ?",
                "À quelles étapes et à quelle fréquence dois-tu voyager ?",
                "Que deviennent les ovocytes, spermatozoïdes ou embryons conservés si tu changes de projet ?",
                "Quel professionnel prendra en charge les urgences et le suivi dans ton pays ?",
                "Quelles preuves soutiennent les éventuelles options complémentaires proposées ?"
              ]
            },
            {
              "title": "Prévoir le retour et les urgences",
              "content": "Avant de partir, identifie où consulter en urgence et comment la clinique qui te traite communiquera avec le professionnel local. Demande quand un voyage est médicalement raisonnable après une ponction ou un transfert, et quels symptômes doivent t'empêcher de continuer le trajet. Les consignes de ta clinique priment ; une douleur intense, des saignements importants, des difficultés respiratoires, un malaise ou un gonflement qui s'aggrave rapidement nécessitent une prise en charge urgente.",
              "bulletPoints": [
                "Service d'urgence ou centre de soins près de ton hébergement",
                "Numéro d'urgence de la clinique et résumé du traitement",
                "Accompagnant connaissant les médicaments et le plan d'urgence",
                "Temps et budget supplémentaires si le retour n'est pas médicalement conseillé"
              ]
            },
            {
              "title": "Checklist pour un parcours à distance",
              "bulletPoints": [
                "Demander le calendrier complet, le fuseau horaire et les fenêtres de déplacement",
                "Confirmer le professionnel local, les examens requis et la transmission sécurisée des dossiers",
                "Obtenir les consignes écrites pour les médicaments, le déclenchement, les retards et les urgences",
                "Emporter les ordonnances, dossiers de traitement et lettre médicale",
                "Calculer le suivi, les médicaments, l'hébergement, les retards, la conservation et le suivi après traitement",
                "Vérifier les règles du pays de destination, des pays de transit, de l'assurance, du don et de la conservation"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-traveling-with-fertility-medications",
      "locale": "fr",
      "variant": {
        "title": "Voyager avec des médicaments de fertilité",
        "subtitle": "Préparer le bagage cabine, la conservation, les contrôles de sécurité, les fuseaux horaires et les retards sans improviser.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Garde les médicaments avec toi.",
              "description": "Mets les médicaments et le matériel en cabine, dans leur emballage étiqueté, avec les ordonnances et une lettre de la clinique."
            },
            {
              "title": "La conservation dépend du produit.",
              "description": "Certains médicaments doivent être réfrigérés, d'autres ne doivent pas être congelés et certains ont une durée précise à température ambiante. Suis l'étiquette et le plan de la clinique."
            },
            {
              "title": "Prépare un plan en cas de retard.",
              "description": "Prends une quantité validée par la clinique pour le voyage et les retards, et sache qui appeler avant de modifier une dose ou de remplacer un médicament exposé à une mauvaise température."
            }
          ],
          "sections": [
            {
              "title": "Commencer par un plan de voyage pour les médicaments",
              "content": "Demande à ta clinique ou à ton pharmacien un plan écrit pour chaque médicament : nom générique, dose, horaire, plage de conservation, durée possible à température ambiante et conduite à tenir après un retard ou un écart de température. Les médicaments de fertilité ne sont pas interchangeables : l'étiquette du produit et les consignes de la clinique priment.",
              "bulletPoints": [
                "Quels médicaments doivent être réfrigérés et lesquels doivent rester à température ambiante ?",
                "Combien de temps chaque médicament peut-il rester hors de sa plage normale ?",
                "Que faire si une dose est en retard, si un flacon casse ou si la glacière ne fonctionne plus ?",
                "Quel matériel faut-il : aiguilles, seringues, compresses, alcool ou collecteur ?"
              ]
            },
            {
              "title": "Mettre les médicaments en cabine",
              "content": "Garde les médicaments, aiguilles, seringues et accessoires de contrôle de température avec toi, plutôt qu'en soute. Utilise les emballages étiquetés et prends les ordonnances ou une liste avec les noms génériques. Prévois la quantité nécessaire et une marge validée par la clinique ; n'envoie pas les médicaments à l'avance et ne suppose pas qu'ils pourront être remplacés sans risque à l'étranger.",
              "bulletPoints": [
                "Emballage d'origine avec ton nom et l'étiquette de prescription",
                "Copies des ordonnances et lettre médicale décrivant les traitements injectables",
                "Médicaments et matériel supplémentaires selon le plan de la clinique",
                "Pochette accessible et sécurisée plutôt que des produits en vrac"
              ]
            },
            {
              "title": "Prévoir les contrôles et les frontières",
              "content": "Les règles de sécurité et d'importation varient selon les pays et peuvent aussi s'appliquer pendant une escale. Si tu pars des États-Unis, la TSA autorise les liquides et accessoires de refroidissement médicalement nécessaires en cabine, mais ils doivent être contrôlés séparément. Vérifie les règles de chaque aéroport et de chaque destination et garde les justificatifs à portée de main.",
              "bulletPoints": [
                "Vérifier les règles du pays de départ et du pays de destination",
                "Vérifier les règles des pays de transit si tu passes la douane pendant une escale",
                "Déclarer les liquides ou fournitures médicalement nécessaires si demandé",
                "Demander conseil à la clinique ou à un spécialiste de médecine des voyages pour les médicaments réglementés"
              ]
            },
            {
              "title": "Respecter la plage de température de l'étiquette",
              "content": "Ne suppose pas que plus froid est toujours plus sûr. Certains médicaments ne doivent pas être congelés, tandis que d'autres ont une durée limitée à température ambiante. Si la réfrigération est requise, demande à la clinique ou au pharmacien quelle glacière, quelle disposition des pains de glace et quelle plage de température conviennent. Évite le contact direct avec des pains de glace congelés sauf indication du produit, et n'utilise pas le compartiment congélateur d'un hôtel.",
              "bulletPoints": [
                "Confirmer la plage de température acceptable avant de partir",
                "Suivre les instructions du produit plutôt qu'une astuce générale de voyage",
                "Demander quoi faire après un gel, une surchauffe, une humidité, un dommage ou un oubli dans une voiture",
                "À l'arrivée, utiliser un réfrigérateur fiable uniquement si le produit le demande"
              ]
            },
            {
              "title": "Gérer les fuseaux horaires avec prudence",
              "content": "Demande à la clinique d'écrire le calendrier dans les fuseaux de départ et de destination, ou d'expliquer précisément la conversion. La clinique doit valider tout changement d'horaire. Les injections de déclenchement et les autres doses sensibles ne doivent jamais être décalées avec une application de voyage ou au jugé.",
              "bulletPoints": [
                "Enregistrer le calendrier de la clinique hors connexion avant le départ",
                "Programmer les alarmes selon le fuseau validé par la clinique",
                "Savoir quoi faire si l'avion traverse un fuseau ou si la connexion est perdue",
                "Appeler la clinique avant de modifier ou de répéter une dose"
              ]
            },
            {
              "title": "En cas de retard ou de problème de conservation",
              "content": "Garde le médicament et son emballage, note ce qui s'est passé et pendant combien de temps, puis contacte la clinique ou le pharmacien qui l'a délivré. Ne jette pas et ne remplace pas une dose, ne double pas une dose et ne poursuis pas avec un médicament exposé à une mauvaise température sans avis. Si la clinique est injoignable, demande rapidement un avis de sécurité à un pharmacien ou à un service de soins local tout en continuant à essayer de joindre l'équipe qui te suit.",
              "bulletPoints": [
                "Retard de vol, bagage perdu, flacon endommagé ou glacière cassée",
                "Médicament laissé dans la chaleur, au soleil, dans une voiture ou dans un environnement gelé",
                "Dose oubliée, en retard ou incertaine",
                "Besoin de matériel de remplacement ou d'un lieu sûr pour les objets piquants"
              ]
            },
            {
              "title": "Prévoir le matériel et l'élimination des aiguilles",
              "content": "Emporte le matériel prévu et demande à l'avance à la clinique, à la pharmacie ou à ton hébergement comment éliminer les objets piquants. Ne laisse pas d'aiguilles usagées en vrac dans une poubelle d'hôtel ou dans un endroit où elles pourraient blesser le personnel. Les règles de transport et d'élimination varient selon les lieux.",
              "bulletPoints": [
                "Aiguilles et seringues adaptées au médicament prescrit",
                "Compresses alcoolisées, gaze, pansements et petit collecteur",
                "Plan pour remplacer le matériel si le séjour se prolonge",
                "Clinique ou pharmacie pouvant reprendre les objets piquants lorsque c'est possible"
              ]
            },
            {
              "title": "Checklist pour les médicaments en voyage",
              "bulletPoints": [
                "Plan écrit des doses, fuseaux horaires, conditions de conservation, retards et urgences",
                "Médicaments étiquetés et copies des ordonnances",
                "Lettre médicale pour les médicaments injectables et le matériel",
                "Place en cabine pour les médicaments, aiguilles, seringues et accessoires de refroidissement",
                "Quantité supplémentaire validée par la clinique en cas de retard",
                "Règles des pays de destination et de transit vérifiées avant le départ"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-choosing-a-clinic-abroad",
      "locale": "fr",
      "variant": {
        "title": "Choisir une clinique de fertilité à l'étranger",
        "subtitle": "Comparer la réglementation, le laboratoire, les taux de réussite, l'éligibilité et le suivi avant de réserver.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Vérifier l'encadrement d'abord.",
              "description": "Identifie l'organisme national qui encadre la clinique et le laboratoire, et ce que ce contrôle signifie concrètement."
            },
            {
              "title": "Comparer des résultats comparables.",
              "description": "Un taux de grossesse annoncé ne correspond pas forcément à ton âge, ton diagnostic, ton traitement ou l'issue qui t'intéresse."
            },
            {
              "title": "Préparer le retour.",
              "description": "Avant le traitement, identifie qui gère les complications, les dossiers, les réclamations et le suivi après ton départ."
            }
          ],
          "sections": [
            {
              "title": "Commencer par la réglementation et le laboratoire",
              "content": "Demande quel organisme public, quelle autorité de licence ou quelle accréditation professionnelle encadre la clinique et son laboratoire. Vérifie la clinique auprès de cet organisme lorsque c'est possible. Le langage marketing ne remplace pas un contrôle indépendant, et les règles peuvent beaucoup varier d'un pays à l'autre.",
              "bulletPoints": [
                "Qui autorise ou inspecte la clinique et le laboratoire ?",
                "Quelles normes concernent la conservation, l'identification, le consentement et les données ?",
                "Le médecin et le laboratoire d'embryologie sont-ils nommés dans le contrat ?",
                "Comment les réclamations et événements indésirables sont-ils examinés ?"
              ]
            },
            {
              "title": "Comprendre le plan avant de comparer les prix",
              "content": "Demande un plan écrit qui explique le diagnostic, le traitement proposé, les médicaments, le suivi, les étapes de laboratoire, la politique de transfert et le nombre de visites prévu. Un forfait moins cher peut exclure des éléments nécessaires dans ta situation.",
              "bulletPoints": [
                "Médicaments, prises de sang, échographies, anesthésie, laboratoire, congélation, conservation et transfert",
                "Ce qui se passe s'il n'y a pas d'ovocytes matures, de blastocystes ou d'embryons transférables",
                "Options complémentaires, caractère facultatif et niveau de preuve",
                "Qui prend les décisions si la réponse diffère du plan"
              ]
            },
            {
              "title": "Lire les taux de réussite avec leur contexte",
              "content": "Demande si un chiffre correspond à un taux de grossesse, de naissance vivante, par transfert, par ponction ou à un résultat cumulatif. Vérifie l'âge, le traitement, le statut de donneur, le stade embryonnaire, la période et le nombre de cas. Un résultat concernant un autre groupe de patientes ne prédit pas ton résultat personnel.",
              "bulletPoints": [
                "Même tranche d'âge et même traitement",
                "Ovocytes personnels ou don d'ovocytes ou de sperme",
                "Transfert frais ou congelé et nombre d'embryons transférés",
                "Tous les cycles ou seulement des résultats sélectionnés"
              ]
            },
            {
              "title": "Vérifier l'éligibilité et le cadre juridique",
              "content": "Confirme que le pays et la clinique peuvent légalement te traiter et proposer le traitement dont tu as besoin. Les règles peuvent différer pour les personnes seules, les couples de même sexe, le don, le PGT, la gestation pour autrui ou le nombre d'embryons transférés. Demande un avis juridique qualifié lorsque le statut du traitement, des donneurs, des embryons ou de l'enfant peut traverser une frontière.",
              "bulletPoints": [
                "Éligibilité et documents nécessaires",
                "Identité, dépistage, consentement et droits futurs concernant le donneur",
                "Filiation, nationalité, enregistrement de la naissance ou gestatrice",
                "Conservation, exportation, importation et destruction"
              ]
            },
            {
              "title": "Demander qui te suivra après ton départ",
              "content": "La clinique doit pouvoir expliquer son contact d'urgence, le parcours de soins, la transmission des dossiers et le calendrier de suivi. Demande qui prendra en charge une complication à domicile et comment cette personne recevra le compte rendu. Ne suppose pas que ton système de santé ou ton assurance coordonnera automatiquement les soins avec une clinique étrangère.",
              "bulletPoints": [
                "Contact d'urgence et consignes en dehors des heures d'ouverture",
                "Compte rendu complet et résultats de laboratoire avant le retour",
                "Personne qui interprète le test de grossesse et les résultats suivants",
                "Gestion des réclamations, remboursements et litiges transfrontaliers"
              ]
            },
            {
              "title": "Checklist pour comparer les cliniques",
              "bulletPoints": [
                "Vérifier l'autorité ou l'accréditation de la clinique et du laboratoire",
                "Demander le plan complet et le devis détaillé",
                "Comprendre les taux de réussite et vérifier qu'ils correspondent à ta situation",
                "Confirmer les règles d'éligibilité, de consentement, de don, de conservation et de filiation",
                "Cartographier les visites obligatoires et le suivi local avant de réserver",
                "Obtenir par écrit le parcours d'urgence, de dossiers et de réclamation"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-remote-monitoring-for-fertility-treatment",
      "locale": "fr",
      "variant": {
        "title": "Suivi à distance pour un traitement de fertilité",
        "subtitle": "Coordonner la clinique qui te traite avec les prises de sang, échographies, dossiers et décisions sensibles au temps.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Le suivi local nécessite un relais clair.",
              "description": "La personne locale, la clinique principale et toi devez savoir qui prescrit, réalise, transmet et interprète chaque résultat."
            },
            {
              "title": "Les examens doivent être compatibles.",
              "description": "Demande quels dosages, mesures échographiques, formats de compte rendu et délais la clinique accepte."
            },
            {
              "title": "La clinique principale dirige les soins.",
              "description": "Le suivi local recueille les informations, mais les changements de médicaments et décisions sensibles au temps viennent de l'équipe qui te traite."
            }
          ],
          "sections": [
            {
              "title": "À quoi peut ressembler un suivi à distance",
              "content": "Le suivi à distance signifie généralement qu'une clinique proche de chez toi réalise des prises de sang ou échographies tandis qu'une autre clinique dirige le traitement. Cela peut réduire les déplacements, mais ajoute de la coordination. Vérifie que les deux équipes acceptent l'organisation avant le début du cycle.",
              "bulletPoints": [
                "Échographie et prises de sang locales avec transmission sécurisée le jour même",
                "Téléconsultations avec la clinique qui te traite",
                "Professionnel local disponible pour des rendez-vous de dernière minute",
                "Déplacement vers la clinique principale uniquement pour les actes nécessaires"
              ]
            },
            {
              "title": "Choisir le professionnel local avec la clinique",
              "content": "Demande si la clinique principale a des partenaires ou des exigences précises. Un centre d'imagerie ou un laboratoire généraliste ne propose pas forcément les délais, mesures ou connaissances nécessaires au suivi de fertilité. Vérifie la disponibilité le week-end et les jours fériés.",
              "bulletPoints": [
                "Le professionnel peut-il proposer les examens nécessaires rapidement ?",
                "Connaît-il le suivi de fertilité et les mesures demandées ?",
                "Peut-il envoyer les résultats de façon sécurisée et parler à la clinique ?",
                "Qui assure le suivi si le professionnel local est fermé ?"
              ]
            },
            {
              "title": "Se mettre d'accord sur la transmission des résultats",
              "content": "Avant le premier rendez-vous, définis qui reçoit le résultat, ce qu'il doit contenir, comment il est identifié et quand il doit arriver. Garde une copie, mais ne compte pas uniquement sur une capture d'écran si la clinique demande un compte rendu officiel.",
              "bulletPoints": [
                "Identité de la patiente et date ou heure du prélèvement ou de l'échographie",
                "Unités, normes, mesures folliculaires et mesure de l'endomètre",
                "Canal sécurisé et personne destinataire",
                "Confirmation que la clinique principale a reçu et examiné le résultat"
              ]
            },
            {
              "title": "Clarifier le fuseau horaire et la chaîne de décision",
              "content": "Demande quel fuseau horaire s'applique aux rendez-vous et médicaments, sous quel délai la clinique répond et qui joindre en dehors des heures d'ouverture. Le professionnel local ne doit pas modifier seul le plan de médicaments sauf si la clinique principale lui a explicitement confié cette responsabilité.",
              "bulletPoints": [
                "Calendrier écrit avec le fuseau sur chaque consigne sensible au temps",
                "Délai prévu pour les résultats et les décisions de traitement",
                "Numéro d'urgence pour un résultat qui ne peut pas attendre",
                "Plan en cas de perte d'internet, de retard ou de fermeture locale"
              ]
            },
            {
              "title": "Prévoir une solution de secours",
              "content": "Demande quoi faire si un rendez-vous local est indisponible, si un compte rendu est retardé, si les mesures sont incomplètes ou si le cycle avance plus vite que prévu. La solution peut être un autre professionnel, un déplacement vers la clinique, un calendrier révisé ou l'annulation du cycle. Les options doivent être expliquées avant le traitement.",
              "bulletPoints": [
                "Deuxième professionnel local ou contact hospitalier",
                "Fenêtre de déplacement pour une visite imprévue",
                "Budget pour une nuit ou un transport supplémentaire",
                "Consignes en cas d'échographie, de prise de sang ou de résultat manqué"
              ]
            },
            {
              "title": "Checklist du suivi à distance",
              "bulletPoints": [
                "La clinique principale a validé le professionnel local",
                "Méthodes, mesures, unités et délais sont écrits",
                "Transmission sécurisée et confirmation de réception sont convenues",
                "Fuseau horaire, autorité sur les médicaments et contacts d'urgence sont clairs",
                "Professionnel de secours et plan de déplacement existent avant le cycle"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-planning-your-fertility-treatment-trip",
      "locale": "fr",
      "variant": {
        "title": "Planifier son déplacement pour un traitement de fertilité",
        "subtitle": "Un guide étape par étape pour les transports, l'hébergement, l'accompagnant, les documents, le suivi et la récupération.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Planifie des fenêtres, pas une seule date.",
              "description": "Le calendrier peut bouger. Prévois une marge autour du suivi, de la ponction, du transfert et du retour."
            },
            {
              "title": "Rendre le séjour praticable.",
              "description": "Hébergement, transport, repas, garde d'enfants, accompagnement et espace de récupération comptent autant que le billet."
            },
            {
              "title": "Préparer le retour.",
              "description": "Repars avec les dossiers, ordonnances, contacts d'urgence et le plan de suivi à domicile."
            }
          ],
          "sections": [
            {
              "title": "Construire la carte du parcours avec la clinique",
              "content": "Demande à la clinique de séparer le parcours en déplacements possibles : consultation, début de cycle, suivi, ponction, transfert, test de grossesse et suivi. Certaines visites peuvent être locales, d'autres nécessitent d'être près de la clinique avec peu de préavis. Ne réserve pas de transport non remboursable avant de comprendre les fenêtres.",
              "bulletPoints": [
                "Arrivée la plus tôt possible, départ le plus tard possible et nuits supplémentaires",
                "Visites pouvant être locales ou en téléconsultation",
                "Préavis attendu avant une ponction ou un transfert",
                "Conduite à tenir si le cycle est annulé ou le transfert reporté"
              ]
            },
            {
              "title": "Réserver avec flexibilité",
              "content": "Si possible, choisis des transports et hébergements modifiables, proches de la clinique ou bien desservis. Demande à la clinique à quelle fréquence les patientes doivent prolonger leur séjour et si les week-ends ou jours fériés changent le suivi ou le laboratoire.",
              "bulletPoints": [
                "Conditions de modification et d'annulation des transports et de l'hébergement",
                "Réserve pour une nuit ou un retour supplémentaire",
                "Temps de trajet vers la clinique à l'heure du rendez-vous",
                "Plan en cas de météo, grève ou maladie"
              ]
            },
            {
              "title": "Choisir un hébergement adapté",
              "content": "Cherche un lieu calme avec un transport fiable, un réfrigérateur adapté uniquement si un médicament le nécessite, un espace de repos et un accès aux repas et aux soins urgents. Demande si un accompagnant est nécessaire après une sédation ou une procédure.",
              "bulletPoints": [
                "Trajet sûr vers la clinique et les urgences locales",
                "Accompagnant pouvant conduire, traduire ou rester avec toi",
                "Cuisine, hydratation, repas et espace de récupération",
                "Garde d'enfants, travail et accessibilité"
              ]
            },
            {
              "title": "Emporter les documents médicaux",
              "content": "Garde avec toi le calendrier, la liste des médicaments, les ordonnances, les allergies, les résultats récents, les coordonnées de la clinique, les informations d'assurance et une lettre médicale pour les médicaments injectables. Conserve des copies sécurisées en ligne et hors connexion. Demande le compte rendu et les résultats de laboratoire avant le retour.",
              "bulletPoints": [
                "Emballages d'origine et copies des ordonnances",
                "Résultats, imageries, comptes rendus et consentements",
                "Coordonnées de la clinique de destination et de l'équipe à domicile",
                "Traduction des informations essentielles si nécessaire"
              ]
            },
            {
              "title": "Prévoir le temps autour de la procédure",
              "content": "La procédure n'est qu'une partie du déplacement. Ajoute les jours de suivi, l'attente des résultats, la récupération et la possibilité d'un changement. Demande si la clinique recommande un délai précis avant le retour.",
              "bulletPoints": [
                "Congés et présence de l'accompagnant",
                "Garde d'enfants, animaux, repas et transports locaux",
                "Jours de récupération et rendez-vous de suivi",
                "Réserve financière pour un retard ou un séjour prolongé"
              ]
            },
            {
              "title": "Checklist du déplacement",
              "bulletPoints": [
                "Obtenir les fenêtres de voyage et le plan en cas de changement",
                "Réserver des transports et un hébergement modifiables",
                "Confirmer l'accompagnant, la garde, l'accessibilité et le repos",
                "Emporter dossiers, ordonnances, médicaments, matériel et contacts",
                "Budgéter suivi, repas, hébergement, retards et suivi après traitement",
                "Prévoir la transmission des résultats à l'équipe à domicile"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-when-travel-plans-change-during-treatment",
      "locale": "fr",
      "variant": {
        "title": "Quand le voyage change pendant le traitement",
        "subtitle": "Que demander en cas de retard, de réponse plus rapide, de transfert annulé, de dose manquée ou de nuit supplémentaire.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Les changements font partie du plan.",
              "description": "Demande à l'avance comment la clinique gère les retards, le suivi supplémentaire, l'annulation ou un transfert déplacé."
            },
            {
              "title": "Appeler avant de modifier un médicament.",
              "description": "Un retard de transport ou une dose manquée doit conduire à contacter l'équipe, pas à improviser un nouvel horaire."
            },
            {
              "title": "Prévoir une marge pratique.",
              "description": "Hébergement, transport, matériel et contact d'urgence supplémentaires peuvent rendre un changement gérable."
            }
          ],
          "sections": [
            {
              "title": "Demander le plan avant le départ",
              "content": "Au début du traitement, demande quels changements sont fréquents et qui te contactera. La clinique doit expliquer quoi faire si les follicules ou l'endomètre évoluent plus ou moins vite, si un résultat arrive en retard ou si une procédure ne peut pas avoir lieu le jour prévu.",
              "bulletPoints": [
                "Qui t'appelle ou t'écrit quand le calendrier change",
                "Le préavis possible",
                "Les décisions qui nécessitent le clinicien qui te traite",
                "Les frais ajoutés si le suivi ou le séjour s'allonge"
              ]
            },
            {
              "title": "En cas de retard de vol ou de train",
              "content": "Contacte la clinique dès que tu connais le retard et donne ta position, ton horaire de médicaments et ta nouvelle heure d'arrivée. Demande si tu dois continuer, modifier ou suspendre une dose, et où consulter si tu as des symptômes pendant le trajet. Ne double pas une dose pour compenser un retard.",
              "bulletPoints": [
                "Garder le numéro d'urgence de la clinique hors connexion",
                "Partager le nouvel itinéraire et le fuseau horaire",
                "Garder médicaments et dossiers avec toi",
                "Demander des consignes écrites si le plan change"
              ]
            },
            {
              "title": "Si le cycle avance ou ralentit",
              "content": "Un résultat de suivi peut entraîner une nouvelle visite locale, une nouvelle date de voyage, un changement de dose, une heure de déclenchement ou l'annulation du cycle. Demande à la clinique d'indiquer le nouveau plan dans ton fuseau horaire et le prochain moment de décision.",
              "bulletPoints": [
                "Prochaine échographie ou prise de sang et délai",
                "Changement de médicament et clinicien responsable",
                "Nouvelle fenêtre de ponction ou de transfert",
                "Conséquences pour l'hébergement, le travail ou l'accompagnant"
              ]
            },
            {
              "title": "Si un transfert est reporté ou annulé",
              "content": "Demande ce qui arrive à l'embryon, aux médicaments, à la conservation et au suivi. Un transfert reporté peut modifier la durée du séjour ou permettre un retour à domicile, mais la clinique doit diriger le calendrier médical et expliquer les nouvelles consignes.",
              "bulletPoints": [
                "Conservation de l'embryon et consentement applicable",
                "Médicaments à poursuivre, arrêter ou modifier",
                "Moment possible du prochain transfert ou bilan",
                "Frais, vols, hébergement et congés concernés"
              ]
            },
            {
              "title": "Si le séjour doit être prolongé",
              "content": "Garde une courte liste de logements, transports, pharmacies et soins locaux. Demande un compte rendu écrit si tu dois consulter une autre équipe et ne prends que les médicaments et le matériel que la clinique a validés.",
              "bulletPoints": [
                "Hébergement prolongé et transport accessible",
                "Remplacement sûr des médicaments et du matériel",
                "Soins urgents ou service d'urgence local",
                "Nouvelles dispositions de travail, de garde et d'accompagnement"
              ]
            },
            {
              "title": "Checklist en cas de changement",
              "bulletPoints": [
                "Contacter la clinique avant de modifier une dose ou un itinéraire",
                "Noter le nouveau fuseau, le calendrier et le prochain point de décision",
                "Demander les conséquences médicales, logistiques et financières",
                "Conserver les appels, consignes, reçus et nouveaux rendez-vous",
                "Savoir où consulter en urgence si des symptômes apparaissent"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-traveling-after-retrieval-or-embryo-transfer",
      "locale": "fr",
      "variant": {
        "title": "Voyager après une ponction ou un transfert d'embryon",
        "subtitle": "Préparer avec la clinique la récupération et le retour après une procédure de fertilité sensible au temps.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Il n'existe pas d'autorisation universelle.",
              "description": "La clinique doit te dire quand voyager selon la procédure, tes symptômes et tes antécédents."
            },
            {
              "title": "Préparer la récupération.",
              "description": "Prévois transport, hydratation, repas, repos, médicaments et accompagnement plutôt qu'un simple passage à la clinique."
            },
            {
              "title": "Connaître les symptômes urgents.",
              "description": "Repars avec des consignes écrites sur les personnes à appeler et le lieu où consulter si ton état se dégrade."
            }
          ],
          "sections": [
            {
              "title": "Demander quand le voyage est raisonnable",
              "content": "Le moment le plus sûr après une ponction ou un transfert dépend de la procédure, de la sédation, de tes symptômes, des complications et du protocole de la clinique. Demande une recommandation écrite qui tient compte de la durée et du type de trajet, de la destination et de l'accès aux soins. Ne prends pas le calendrier d'une autre patiente comme autorisation.",
              "bulletPoints": [
                "Moment où tu peux quitter la clinique et la région",
                "Besoin d'un accompagnant après une sédation ou une procédure",
                "Activités, charges, exercice ou conditions de voyage à éviter",
                "Évaluation locale nécessaire avant le trajet"
              ]
            },
            {
              "title": "Organiser le retour autour de la récupération",
              "content": "Demande comment voyager confortablement et quel soutien prévoir. Un trajet direct avec accès à l'eau, aux repas, aux médicaments et à un accompagnant peut être plus simple qu'une longue correspondance, mais la clinique doit te conseiller selon ta situation. Prévois de pouvoir t'arrêter ou consulter si tu ne te sens pas bien.",
              "bulletPoints": [
                "Trajet direct ou modifiable lorsque c'est possible",
                "Accompagnant connaissant les consignes de la clinique",
                "Médicaments, dossiers, eau, repas et vêtements confortables",
                "Lieu de soins urgents sur le trajet ou près du domicile"
              ]
            },
            {
              "title": "Après une ponction",
              "content": "Une ponction est une procédure qui peut entraîner sédation, crampes, saignements, ballonnements ou complications comme une hyperstimulation. Demande quels symptômes sont attendus, lesquels nécessitent un appel et lesquels sont urgents. Si tu ne récupères pas comme prévu, contacte la clinique avant de poursuivre le trajet.",
              "bulletPoints": [
                "Restrictions après sédation ou anesthésie et besoin d'un accompagnant",
                "Douleur, saignements, ballonnements et médicaments attendus",
                "Diminution des urines, difficultés respiratoires, douleur intense ou gonflement rapide",
                "Personne pouvant t'évaluer si tu es loin de la clinique"
              ]
            },
            {
              "title": "Après un transfert d'embryon",
              "content": "Demande quels médicaments poursuivre, quand faire le test et comment le suivi se fera à domicile. Le trajet ou les mouvements habituels ne remplacent pas les consignes individuelles de la clinique ; tout symptôme inquiétant doit être signalé.",
              "bulletPoints": [
                "Horaire des médicaments et fuseau horaire de chaque dose",
                "Date du test de grossesse et transmission du résultat",
                "Saignements, douleurs, fièvre ou autres symptômes nécessitant un appel",
                "Professionnel responsable des urgences et du suivi au retour"
              ]
            },
            {
              "title": "Checklist de récupération et de retour",
              "bulletPoints": [
                "Obtenir les consignes écrites sur le voyage et les activités",
                "Prévoir un accompagnant si la sédation, les symptômes ou la distance le justifient",
                "Garder médicaments, dossiers, numéros d'urgence et matériel accessibles",
                "Connaître les lieux de soins urgents près de l'hébergement et du domicile",
                "Contacter la clinique si les symptômes s'aggravent ou si la récupération diffère"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-fertility-treatment-travel-costs-and-insurance",
      "locale": "fr",
      "variant": {
        "title": "Coûts et assurance pour un traitement de fertilité en voyage",
        "subtitle": "Calculer tout le parcours, vérifier les exclusions et prévoir une réserve pour les retards ou complications.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Le prix du forfait n'est pas celui du voyage.",
              "description": "Ajoute médicaments, suivi, transports, hébergement, repas, garde, conservation et suivi au devis de la clinique."
            },
            {
              "title": "Demander les exclusions.",
              "description": "Traitement de fertilité, maladie préexistante, complication, annulation et évacuation peuvent être traités différemment."
            },
            {
              "title": "Prévoir une réserve.",
              "description": "Une marge peut couvrir un suivi supplémentaire, des nuits, un transfert reporté ou un besoin médical imprévu."
            }
          ],
          "sections": [
            {
              "title": "Demander le devis complet",
              "content": "Demande à la clinique de séparer le traitement nécessaire des options complémentaires et d'indiquer ce qui n'est pas inclus. Compare les devis avec les mêmes hypothèses de traitement, médicaments, suivi, laboratoire, conservation et suivi après traitement.",
              "bulletPoints": [
                "Consultation, examens, médicaments, suivi, anesthésie, ponction, transfert et laboratoire",
                "Congélation, conservation, PGT, don et expédition",
                "Suivi local et traduction",
                "Annulation, remboursement et transfert reporté"
              ]
            },
            {
              "title": "Calculer les jours autour du traitement",
              "content": "Ajoute chaque jour où tu peux devoir rester, y compris le suivi, l'attente des résultats, la récupération et une nuit supplémentaire. Intègre le voyage et les congés de l'accompagnant lorsqu'il est nécessaire.",
              "bulletPoints": [
                "Vols, trains, transports locaux, parking et transferts",
                "Hébergement, repas, accessibilité et réfrigérateur adapté si nécessaire",
                "Congés, garde d'enfants, animaux et accompagnant",
                "Réserve pour un retard, un changement de retour ou une procédure annulée"
              ]
            },
            {
              "title": "Vérifier séparément l'assurance médicale et voyage",
              "content": "Demande à la clinique et à l'assureur ce qui est couvert. Une assurance voyage peut exclure les pathologies préexistantes, les soins planifiés, les complications d'un traitement de fertilité ou les soins hors réseau. Pour un traitement à l'étranger, demande si l'urgence, le rapatriement et l'évacuation médicale sont inclus.",
              "bulletPoints": [
                "Traitement à l'étranger et complications liées aux soins prévus",
                "Pathologies préexistantes et procédures récentes",
                "Annulation ou interruption du voyage",
                "Soins urgents, rapatriement et évacuation médicale"
              ]
            },
            {
              "title": "Demander l'effet d'un changement sur la facture",
              "content": "Avant de payer, demande ce qui est dû si le suivi augmente, si le cycle est annulé, si la ponction ne donne pas d'ovocytes utilisables ou si le transfert passe au cycle suivant. Obtiens la réponse par écrit et garde les reçus.",
              "bulletPoints": [
                "Échographies, prises de sang, médicaments, nuits ou déplacements supplémentaires",
                "Cycle annulé, reporté ou converti",
                "Suivi dans le pays de destination ou à domicile",
                "Conservation et utilisation future"
              ]
            },
            {
              "title": "Garder les dossiers financiers et médicaux ensemble",
              "content": "Conserve le devis, les factures, les ordonnances, les reçus, le compte rendu et les échanges avec l'assurance. Garde des copies accessibles pour une réclamation ou pour expliquer les soins à un professionnel à domicile.",
              "bulletPoints": [
                "Contrat de la clinique et devis détaillé",
                "Reçus de traitement, médicaments, transport et hébergement",
                "Numéro de police et contact d'assistance 24 h/24",
                "Dossiers de traitement et documents d'urgence"
              ]
            },
            {
              "title": "Checklist des coûts de voyage",
              "bulletPoints": [
                "Obtenir un devis écrit séparant le nécessaire et l'optionnel",
                "Ajouter suivi, médicaments, transports, hébergement, repas et congés",
                "Vérifier les exclusions de traitement, complication, annulation et évacuation",
                "Demander l'effet financier d'un changement de cycle ou de voyage",
                "Créer une réserve et conserver tous les reçus"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-moving-frozen-eggs-sperm-or-embryos-across-borders",
      "locale": "fr",
      "variant": {
        "title": "Déplacer des ovocytes, spermatozoïdes ou embryons congelés",
        "subtitle": "Pourquoi le transport de matériel cryoconservé est organisé par les cliniques et diffère du transport de médicaments.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "Ne pas transporter soi-même le matériel congelé.",
              "description": "Les ovocytes, spermatozoïdes et embryons doivent passer par les cliniques et un transport spécialisé de matériel biologique."
            },
            {
              "title": "Des autorisations et consentements peuvent être nécessaires.",
              "description": "Les règles dépendent des pays, des cliniques, du statut de donneur, des consentements et du matériel déplacé."
            },
            {
              "title": "Commencer tôt.",
              "description": "Vérifications, documents, transporteur et autorisations peuvent prendre plus de temps qu'un voyage normal."
            }
          ],
          "sections": [
            {
              "title": "Un transfert de clinique à clinique",
              "content": "Les ovocytes, spermatozoïdes et embryons congelés sont du matériel biologique, pas des bagages ordinaires. Commence par la clinique d'envoi et celle qui reçoit. Elles doivent confirmer qu'elles peuvent travailler ensemble, quelles autorisations s'appliquent et quel transporteur spécialisé maintiendra les conditions nécessaires.",
              "bulletPoints": [
                "La clinique d'envoi a identifié et libéré le matériel avec les consentements valides",
                "La clinique d'arrivée peut recevoir, conserver et utiliser le matériel",
                "Les deux cliniques connaissent les exigences du pays de destination",
                "Un transporteur spécialisé est organisé par les cliniques"
              ]
            },
            {
              "title": "Confirmer l'identité, la traçabilité et la conservation",
              "content": "Demande comment les cliniques vérifient l'identité et le nombre d'échantillons, documentent chaque remise, protègent le conteneur et contrôlent l'arrivée. Demande où le matériel sera conservé, pendant combien de temps et que se passe-t-il si le transport est retardé ou refusé.",
              "bulletPoints": [
                "Identifiants uniques et étiquetage dans les deux cliniques",
                "Remises documentées et suivi du transporteur",
                "Conteneur adapté au transport cryogénique",
                "Contrôle à l'arrivée et confirmation de réception"
              ]
            },
            {
              "title": "Vérifier les consentements et les règles du don",
              "content": "Le consentement à la conservation, au transport, au traitement, au don et à l'utilisation future peut être distinct. Le matériel de donneur peut avoir des exigences supplémentaires d'identité, de dépistage, de consentement et de conservation. Ne suppose pas qu'un consentement valable dans un pays suffit dans un autre.",
              "bulletPoints": [
                "Consentement des personnes concernées par les ovocytes, spermatozoïdes ou embryons",
                "Dates de renouvellement et d'expiration de la conservation",
                "Autorisation d'exporter, importer, traiter, donner ou utiliser pour la recherche",
                "Informations sur le donneur et règles d'identité ou de divulgation"
              ]
            },
            {
              "title": "Demander le calendrier et le coût",
              "content": "Demande un calendrier qui inclut l'examen par les cliniques, les documents, les autorisations, la réservation du transporteur, les douanes et l'acceptation par la clinique d'arrivée. Demande le coût total, y compris les frais des cliniques, du transporteur, de conservation, de traduction et d'autorisation.",
              "bulletPoints": [
                "Première date réaliste d'expédition",
                "Ce qui peut retarder ou empêcher le transfert",
                "Assurance ou responsabilité en cas de dommage ou de refus",
                "Qui paie la conservation ou une seconde expédition"
              ]
            },
            {
              "title": "Checklist du transport cryogénique",
              "bulletPoints": [
                "Contacter les deux cliniques avant de réserver un voyage ou un traitement",
                "Confirmer les règles de pays, clinique, donneur, consentement et conservation",
                "Utiliser un transporteur spécialisé organisé par les cliniques",
                "Obtenir par écrit la traçabilité et la confirmation de réception",
                "Budgéter autorisations, transport, traduction, conservation et retards",
                "Garder les documents de consentement, transport et conservation"
              ]
            }
          ]
        }
      }
    },
    {
      "id": "moon-article-donor-and-carrier-treatment-across-borders",
      "locale": "fr",
      "variant": {
        "title": "Don et gestation pour autrui à l'étranger",
        "subtitle": "Questions sur le consentement, le dépistage, la gestatrice, la filiation et le suivi lorsque les soins traversent des pays.",
        "body": {
          "introduction": "Voyage",
          "quickSummary": [
            {
              "title": "L'avis juridique fait partie du parcours.",
              "description": "Les règles peuvent différer entre le pays de destination et le pays de résidence, notamment pour le don, la gestatrice, le consentement et la filiation."
            },
            {
              "title": "Demander comment chacun est protégé.",
              "description": "Dépistage, accompagnement, consentement, rémunération, soins et urgences doivent être clairs pour les donneurs, gestatrices et futurs parents."
            },
            {
              "title": "Préparer le retour de l'enfant.",
              "description": "Filiation, nationalité, enregistrement de la naissance, documents de voyage et dossiers médicaux peuvent demander des démarches séparées."
            }
          ],
          "sections": [
            {
              "title": "Commencer par un avis juridique indépendant",
              "content": "Lorsqu'un don ou une gestatrice traverse une frontière, consulte des juristes qualifiés dans le pays de destination et dans ton pays de résidence. La clinique peut expliquer son processus médical, mais elle n'est pas forcément responsable de la filiation, de la nationalité, de l'immigration ou de ton contrat après ton départ.",
              "bulletPoints": [
                "Qui est reconnu comme parent légal et à quel moment",
                "Caractère applicable des accords avec le donneur ou la gestatrice",
                "Enregistrement de la naissance, nationalité, passeport et retour",
                "Conduite à tenir si le traitement, la naissance ou le voyage est retardé"
              ]
            },
            {
              "title": "Demander les règles d'identité et de consentement du donneur",
              "content": "Les règles du don varient selon le pays. Demande comment les donneurs sont recrutés et dépistés, quelles informations sont conservées, si le don est anonyme ou avec identité accessible, comment le consentement peut évoluer et comment le matériel peut être conservé ou déplacé. Pense à l'effet sur le futur enfant, pas seulement sur le cycle.",
              "bulletPoints": [
                "Dépistage infectieux, génétique, médical et familial",
                "Informations médicales et antécédents familiaux",
                "Identité, divulgation, fratries et contacts futurs",
                "Consentement à la conservation, à l'exportation et à l'utilisation future"
              ]
            },
            {
              "title": "Comprendre le dépistage et le soutien de la gestatrice",
              "content": "Un parcours avec gestatrice devrait inclure un dépistage médical, un accompagnement psychologique, un consentement éclairé, une représentation juridique indépendante et un plan clair pour la grossesse et les complications. Demande qui coordonne les soins lorsque la gestatrice et les futurs parents vivent dans des lieux différents.",
              "bulletPoints": [
                "Dépistage médical et psychologique de la gestatrice",
                "Conseil juridique indépendant et accompagnement pour chaque partie",
                "Suivi prénatal, urgences et lieu de l'accouchement",
                "Plan en cas de grossesse multiple, perte ou complication inattendue"
              ]
            },
            {
              "title": "Préparer les dossiers, la communication et les urgences",
              "content": "Assure-toi que la clinique, l'équipe du donneur ou de la gestatrice, les futurs parents, les professionnels à domicile et les juristes savent qui peut accéder à quels dossiers. Identifie les contacts d'urgence et le professionnel responsable à chaque étape. Garde les dossiers et consentements dans les langues utiles aux équipes de soins.",
              "bulletPoints": [
                "Liste de contacts partagée et plan en dehors des heures d'ouverture",
                "Accès sécurisé aux dossiers et traduction",
                "Déplacements et hébergement pour les rendez-vous ou complications",
                "Soutien psychologique pour donneurs, gestatrices, futurs parents et partenaires"
              ]
            },
            {
              "title": "Checklist du parcours avec tiers",
              "bulletPoints": [
                "Obtenir un avis juridique indépendant dans les deux pays concernés",
                "Confirmer dépistage, accompagnement, consentement et rémunération",
                "Demander les règles d'identité, divulgation, conservation et utilisation future",
                "Préparer filiation, nationalité, enregistrement et documents de voyage",
                "Créer un plan médical, de communication, d'urgence et de soutien psychologique"
              ]
            }
          ]
        }
      }
    }
  ]
}
